<!--
  ConversationHeader — Phase 4-6 / T32.

  Sticky bar above the message thread.

  Layout per impeccable T32:
    [ back ‹ ]  [ name + FB-MESSENGER badge ]  [ time ]  [ Create ▾ ]  [ ⋯ ]

  - Customer name pulled from host-summary when matched; falls back to
    "Unmatched contact" (italic + muted) otherwise.
  - Channel badge is text-only, accent-tinted.
  - Last-activity time is relative ("2m ago").
  - "Create" is the primary CRA: filled accent button opening a dropdown of
    entity types from `session.state.sso.sso.capabilities.entityTypes`.
    Selecting an item invokes the `onCreateSelect` callback prop with the
    full capability descriptor; ThreadPage opens CreateEntityModal in
    response (Phase 4-7 / T35).
  - Overflow button is a visual placeholder in v1 (no menu).
-->
<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import type { Conversation, HostSummary, SsoEntityTypeCapability } from '../lib/api/queries.js';
  import { setAssigneeMutation } from '../lib/api/mutations.js';
  import { session } from '../lib/stores/session.svelte.js';
  import { router } from '../lib/router/route.svelte.js';
  import { getPresence } from '../lib/realtime/presence.svelte.js';
  import { computeAssigneeButtonState } from '../lib/assignee/button-state.js';
  import NotificationToggle from './NotificationToggle.svelte';
  // Tag picker + manage modal moved into CustomerContextPanel
  // (right-panel reshuffle); header no longer renders them.

  type Props = {
    conversation: Conversation | null;
    hostSummary: HostSummary | null;
    /**
     * Invoked when the operator picks an entity type from the Create ▾ menu.
     * ThreadPage uses this to open the CreateEntityModal (T35).
     */
    onCreateSelect?: (selection: {
      entityType: string;
      displayName: string;
      formSchema: unknown;
    }) => void;
    /**
     * Finish the conversation. Absent — or a conversation already closed —
     * hides the button; reopening lives on the bar that replaces the
     * composer, where the operator is already looking.
     */
    onClose?: () => void;
    closing?: boolean;
  };
  let { conversation, hostSummary, onCreateSelect, onClose, closing = false }: Props = $props();

  // Display name: prefer host-summary, then conversation.customerName, else fallback.
  const matchedName = $derived(hostSummary?.summary?.displayName?.trim() ?? '');
  const convName = $derived(conversation?.customerName?.trim() ?? '');
  const fallback = 'Unmatched contact';
  const displayName = $derived(matchedName || convName || fallback);
  const isUnmatched = $derived(!matchedName && !convName);

  const lastAt = $derived(conversation?.lastMessageAt ?? null);
  const relativeTime = $derived(lastAt ? formatRelative(lastAt) : '');

  const entityTypes = $derived<SsoEntityTypeCapability[]>(
    session.state.sso?.sso?.capabilities?.entityTypes ?? [],
  );
  const canCreate = $derived(entityTypes.length > 0);

  let menuOpen = $state(false);
  let menuEl: HTMLDivElement | null = $state(null);
  let triggerEl: HTMLButtonElement | null = $state(null);

  function toggleMenu() {
    menuOpen = !menuOpen;
  }

  function closeMenu() {
    menuOpen = false;
  }

  function selectEntityType(t: SsoEntityTypeCapability) {
    closeMenu();
    // Phase 4-7 / T35: invoke the parent callback. ThreadPage mounts the
    // CreateEntityModal in response.
    onCreateSelect?.({
      entityType: t.entityType,
      displayName: t.displayName,
      formSchema: t.formSchema,
    });
  }

  function back() {
    router.navigateToList();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && menuOpen) {
      closeMenu();
      triggerEl?.focus();
    }
  }

  function handleDocClick(event: MouseEvent) {
    if (!menuOpen) return;
    const target = event.target as Node | null;
    if (!target) return;
    if (menuEl?.contains(target) || triggerEl?.contains(target)) return;
    closeMenu();
  }

  $effect(() => {
    if (typeof document === 'undefined') return;
    document.addEventListener('click', handleDocClick);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('click', handleDocClick);
      document.removeEventListener('keydown', handleKeydown);
    };
  });

  // M2.12 — assign/release header button. Keeps the AssigneePicker
  // (CustomerContextPanel) unchanged for "pick someone else"; this
  // button is the one-click "give me this now" affordance.
  //
  // State machine (current operator vs. server state):
  //   no conversation            → button hidden
  //   unassigned                 → "Assign to me"
  //   mine                       → "Release"
  //   held by someone else       → "Take over" (server answers 409 while
  //                                they still hold it — the operator should
  //                                see that, not a missing button)
  const conversationId = $derived(conversation?.id ?? null);
  const currentUserId = $derived(session.state.sso?.user?.id ?? null);
  // No clock in here any more: an assignment does not change on its own,
  // so nothing needs re-rendering on a tick.
  const assigneeButtonState = $derived(
    computeAssigneeButtonState({
      conversationId,
      currentUserId,
      assignee: conversation?.assignee ?? null,
    }),
  );

  const queryClient = useQueryClient();
  // Mutation rebinds when conversationId changes — derived ensures the
  // cache-key invalidation targets the right conversation.
  const setAssignee = $derived(
    conversationId
      ? createMutation(setAssigneeMutation(queryClient, conversationId))
      : null,
  );
  let assigneeError = $state<string | null>(null);

  async function onAssigneeClick() {
    const state = assigneeButtonState;
    if (state.mode === 'hidden') return;
    if (!conversationId) return;
    // Auto-subscription requires the `$` prefix on the top-level store
    // name — Svelte rejects `$<local-alias>`. Dereference here so the
    // local `mut` is a plain object for the mutateAsync call below.
    const mut = $setAssignee;
    if (!mut) return;
    assigneeError = null;
    try {
      switch (state.mode) {
        case 'assign':
        case 'takeover':
          if (!currentUserId) return;
          await mut.mutateAsync({ userId: currentUserId });
          return;
        case 'release':
          await mut.mutateAsync({ userId: null });
          return;
      }
    } catch (err) {
      assigneeError = err instanceof Error ? err.message : 'Could not update the assignee.';
    }
  }

  function formatRelative(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diff = Math.max(0, Date.now() - then);
    const min = 60_000;
    const hr = 60 * min;
    const day = 24 * hr;
    if (diff < min) return 'just now';
    if (diff < hr) return `${Math.floor(diff / min)}m ago`;
    if (diff < day) return `${Math.floor(diff / hr)}h ago`;
    if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function hashHue(input: string): number {
    let h = 0;
    for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) | 0;
    return Math.abs(h) % 360;
  }

  // M5.7 viewer-presence pill. App.svelte installs the tracker as a module
  // singleton once realtime is up; we read peers reactively. Pre-realtime
  // (early mount) getPresence() returns null and the pill stays hidden.
  const presence = $derived(getPresence());
  const peers = $derived(presence?.peers ?? []);

  const peersLabel = $derived.by(() => {
    const ps = peers;
    if (ps.length === 0) return '';
    const names = ps.map((p) => p.displayName.trim() || 'Operator');
    if (names.length === 1) return `${names[0]} is also viewing`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are also viewing`;
    return `${names[0]}, ${names[1]} +${names.length - 2} more are also viewing`;
  });
</script>

<header class="bar">
  <button type="button" class="back" onclick={back} aria-label="Back to conversations">
    <span class="glyph" aria-hidden="true">‹</span>
  </button>

  <div class="title">
    <span class="name" class:unmatched={isUnmatched} title={displayName}>{displayName}</span>
    <span class="channel" aria-label="Facebook Messenger channel">FB Messenger</span>
  </div>

  {#if peers.length > 0}
    <!-- M5.8 Slice C: role=status + aria-live=polite so screen readers
         announce when a peer joins or leaves the conversation. The pill
         updates reactively as PresenceTracker pushes new viewer lists. -->
    <span class="viewers" role="status" aria-live="polite" title={peersLabel}>
      <span class="viewers-dot" aria-hidden="true"></span>
      {peersLabel}
    </span>
  {/if}

  {#if relativeTime}
    <span class="time" title={lastAt ?? ''}>{relativeTime}</span>
  {/if}

  <div class="actions">
    <!-- M5.3.b — Notification toggle on the thread route. The toggle is
         also mounted in ConversationsPage; both instances share the
         same Notification.permission read, so seeing one render
         "default" implies the other does too. Component self-hides
         when the browser doesn't expose push, when permission is
         already granted (after a brief "Notifications on" indicator),
         or when an iframe host hasn't delegated the permission. -->
    <NotificationToggle />
    {#if onClose && conversation && conversation.status !== 'CLOSED'}
      <!-- The end of the work, next to the button that starts it. Hidden on a
           conversation that is already finished: reopening lives on the bar
           that replaced the composer, where the operator is already looking. -->
      <button type="button" class="claim" disabled={closing} onclick={() => onClose()}>
        {closing ? 'Closing…' : 'Close'}
      </button>
    {/if}
    {#if assigneeButtonState.mode !== 'hidden'}
      <button
        type="button"
        class="claim"
        class:claim-mine={assigneeButtonState.mode === 'release'}
        disabled={$setAssignee?.isPending ?? false}
        title={assigneeError ?? undefined}
        aria-label={assigneeButtonState.label}
        onclick={() => void onAssigneeClick()}
      >
        {assigneeButtonState.label}
      </button>
    {/if}
    {#if canCreate}
      <div class="create-wrap">
        <button
          bind:this={triggerEl}
          type="button"
          class="create"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onclick={toggleMenu}
        >
          Create<span class="caret" aria-hidden="true">▾</span>
        </button>
        {#if menuOpen}
          <div class="menu" bind:this={menuEl} role="menu" aria-label="Create entity">
            {#each entityTypes as type (type.entityType)}
              {@const hue = hashHue(type.entityType)}
              <button
                type="button"
                class="menu-item"
                role="menuitem"
                onclick={() => selectEntityType(type)}
              >
                <span class="m-type" style="--type-fg: oklch(48% 0.16 {hue})">{type.entityType}</span>
                <span class="m-name">{type.displayName}</span>
              </button>
            {/each}
          </div>
        {/if}
      </div>
    {/if}

    <button type="button" class="more" aria-label="More options" disabled>
      <span aria-hidden="true">⋯</span>
    </button>
  </div>
</header>

<style>
  .bar {
    display: grid;
    /* back | title | viewers? | time? | actions — the auto cols collapse
       cleanly when absent, so the title column always flexes regardless
       of which optional pills are present. (Tags moved into
       CustomerContextPanel — right-panel reshuffle.) */
    grid-template-columns: auto 1fr auto auto auto;
    align-items: center;
    gap: 12px;
    padding: 0 14px;
    height: 52px;
    background: var(--widget-bg, oklch(99% 0.003 250));
    border-bottom: 1px solid var(--widget-border, oklch(92% 0.005 250));
    flex: 0 0 auto;
    position: relative;
    z-index: 2;
  }

  /* M5.7 — peer-viewer pill. Tinted with the widget accent so it reads as
     a soft status, not a warning. White-space:nowrap keeps the copy on one
     line; in extreme overflow the grid will compress the title column,
     which is acceptable since the badge is short-lived per-thread state. */
  .viewers {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 9px;
    border-radius: 999px;
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 14%, transparent);
    color: var(--widget-accent-deep, oklch(50% 0.19 65));
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
  }
  .viewers-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--widget-accent-deep, oklch(50% 0.19 65));
    box-shadow: 0 0 0 2px color-mix(in oklch, var(--widget-accent-deep, oklch(50% 0.19 65)) 25%, transparent);
  }

  .back {
    background: none;
    border: none;
    color: var(--widget-fg, oklch(20% 0.01 250));
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  }
  .back:hover {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 6%, transparent);
  }
  .back:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
  }
  .back .glyph {
    font-size: 20px;
    line-height: 1;
    font-weight: 500;
  }

  .title {
    min-width: 0;
    display: inline-flex;
    align-items: baseline;
    gap: 10px;
  }
  .name {
    font-size: 14px;
    font-weight: 600;
    color: var(--widget-fg, oklch(20% 0.01 250));
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  .name.unmatched {
    font-style: italic;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .channel {
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 700;
    color: var(--widget-channel-fb, oklch(58% 0.18 260));
    white-space: nowrap;
  }

  .time {
    font-size: 11px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    white-space: nowrap;
  }

  .actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  /* M2.12 — claim/release/refresh button. Outlined (not filled) so it
     reads as a secondary action next to the filled-accent Create CTA.
     `.claim-mine` switches to the accent fill to signal "you own this
     row" at a glance — matches the .chip-assignee.mine pattern.
     `.claim-blocked` mutes the chrome since the button is disabled
     and the label carries the only information that matters. */
  .claim {
    height: 30px;
    padding: 0 12px;
    border-radius: var(--widget-radius-action, 5px);
    background: transparent;
    color: var(--widget-fg, oklch(20% 0.01 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition:
      background 100ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 100ms cubic-bezier(0.22, 1, 0.36, 1);
    font-variant-numeric: tabular-nums;
  }
  .claim:hover:not(:disabled) {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 4%, transparent);
    border-color: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 18%, transparent);
  }
  .claim:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
  }
  .claim:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
  .claim.claim-mine {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 12%, transparent);
    color: var(--widget-accent-deep, oklch(50% 0.19 65));
    border-color: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 28%, transparent);
  }
  .claim.claim-mine:hover:not(:disabled) {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 18%, transparent);
    border-color: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 42%, transparent);
  }
  .create-wrap {
    position: relative;
  }
  .create {
    height: 30px;
    padding: 0 12px;
    border-radius: var(--widget-radius-action, 5px);
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 12%, transparent);
    color: var(--widget-accent, oklch(75% 0.165 75));
    border: 1px solid color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 28%, transparent);
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition:
      background 100ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 100ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .create:hover {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 18%, transparent);
    border-color: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 42%, transparent);
  }
  .create:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
  }
  .caret {
    font-size: 10px;
    line-height: 1;
    transform: translateY(-0.5px);
  }

  .menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 220px;
    background: var(--widget-bg, oklch(99% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: var(--widget-radius, 8px);
    box-shadow:
      0 1px 2px oklch(20% 0.01 250 / 0.06),
      0 8px 24px oklch(20% 0.01 250 / 0.12);
    padding: 6px;
    display: flex;
    flex-direction: column;
    z-index: 10;
  }
  .menu-item {
    background: none;
    border: none;
    text-align: left;
    padding: 0 10px;
    height: 36px;
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 10px;
    border-radius: 8px;
    cursor: pointer;
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .menu-item:hover {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 5%, transparent);
  }
  .menu-item:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: -2px;
  }
  .m-type {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--type-fg, var(--widget-fg-muted, oklch(52% 0.012 250)));
  }
  .m-name {
    font-size: 13px;
    color: var(--widget-fg, oklch(20% 0.01 250));
  }

  .more {
    width: 30px;
    height: 30px;
    background: none;
    border: none;
    border-radius: 8px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .more:hover:not(:disabled) {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 6%, transparent);
  }
  .more:disabled {
    cursor: default;
    opacity: 0.55;
  }
</style>
